import { Controller } from "@hotwired/stimulus"
import { loadMapEngine, getPixelRatio, setupDprResize, quietMarker, LOCAL_CJK_FONT } from "./map_engine"
import { createLogger } from "../lib/logger"

// Non-interactive self-hosted MapLibre mini-map — the self-hosted replacement for Google Static
// Maps thumbnails on product/listing detail pages. Renders a small fixed map centered on the
// item's coordinates with a marker (or, with approximateM, a radius around a snapped public location).
// interactive:false makes it a static thumbnail (no pan/zoom). Each thumbnail is its own WebGL context
// plus a style/tile load, so it mounts only once it comes near the viewport.
// Only mounted when SELF_HOSTED_MAP is on (the view branches on self_hosted_map?).
const log = createLogger('StaticMap')

// Module-level cache: every thumbnail on a page shares ONE style.json fetch (was N fetches for N
// thumbnails in a grid). The parsed style is cloned per-mount so MapLibre can't mutate the shared copy.
let _styleFetch = null
function fetchSharedStyle(url) {
  if (!_styleFetch) _styleFetch = fetch(url).then((r) => r.json())
  return _styleFetch
}

export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    styleUrl: String,
    zoom: { type: Number, default: 15 },
    approximateM: Number,
    arrival: Boolean
  }

  // Approximate thumbnails zoom out a little so the whole radius fits the 12rem-high frame
  static APPROXIMATE_MAX_ZOOM = 14.5

  // Layers stripped from the thumbnail. The mini-map shows the same POIs as the full map (MRT
  // stations + schools + landmarks via "poi", curated landmark schools via "toast_school", and 里
  // names via "toast_village_label") so buyers get the neighborhood context. Only two layers are
  // dropped: building_3d (keeps the map FLAT top-down even though the camera is at pitch 0) and
  // toast_transit_label (MRT line-NAME labels — the heaviest clutter on a tiny map; the stations
  // themselves still render via "poi"). NOTE: ids match the current Map::StyleBuilder. "poi" and
  // "toast_school" are kept together because "poi" deliberately excludes the curated landmark
  // schools (StyleBuilder dedup) — hiding "toast_school" would drop exactly 台大/北一女/… and nothing
  // else, contradicting "show schools".
  static FLAT_HIDDEN_LAYERS = [ "building_3d", "toast_transit_label" ]

  connect() {
    if (!this.hasLatValue || !this.hasLngValue) return
    if (!("IntersectionObserver" in window)) {
      this.mount()
      return
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.observer.disconnect()
      this.observer = null
      this.mount()
    }, { rootMargin: "300px 0px" })
    this.observer.observe(this.element)
  }

  async mount() {
    try {
      this.engine = await loadMapEngine(true) // shares the maplibregl module + pmtiles protocol
    } catch (e) {
      log.error("Failed to load map engine for static thumbnail", e)
      this.element.closest(".static-map-frame")?.classList.add("is-failed")
      return
    }
    if (!this.element.isConnected) return

    // Build a FLAT thumbnail style: fetch the shared style and drop the 3D/POI layers up front
    // (no load-flash). Fall back to the plain URL if the fetch fails.
    let style = this.styleUrlValue
    try {
      const json = await fetchSharedStyle(this.styleUrlValue)
      const hidden = this.constructor.FLAT_HIDDEN_LAYERS
      // Clone the shared style (and filter the layers) so MapLibre can't mutate the cached copy.
      style = structuredClone({ ...json, layers: json.layers.filter((l) => !hidden.includes(l.id)) })
    } catch (e) {
      log.error("Failed to fetch style for flat thumbnail; using full style", e)
      _styleFetch = null // reset so a failed fetch can be retried on the next mount
    }
    if (!this.element.isConnected) return

    this.map = new this.engine.mapClass({
      container: this.element,
      style,
      center: [ this.lngValue, this.latValue ], // [lng, lat]
      zoom: this.zoom,
      pitch: 0,                                 // flat top-down (no 3D tilt)
      bearing: 0,                               // north-up
      interactive: false,                       // static thumbnail — no pan/zoom/rotate
      attributionControl: false,                // legal credits live in Terms/Privacy, not over the thumbnail
      // antialias stays false (MapLibre default): protects REAL iOS Safari from the WebKit MSAA-discard bug
      // (mapbox-gl-js #11609) that blurs an idle map on iOS 15.4+ (not the cause of the SDF text softness).
      pixelRatio: getPixelRatio(), // capped hi-DPR backing store (shared via map_engine)
      // CJK 繁體中文 via on-device TinySDF 48px (marginally sharper than the 24px server SDF); Latin stays server-SDF.
      localIdeographFontFamily: LOCAL_CJK_FONT
    })
    if (this.hasApproximateMValue) {
      // Snapped public location: a soft radius, never a pin that reads as an exact spot
      const area = document.createElement("div")
      area.className = "static-map-area"
      const diameter = 2 * this.approximateMValue / this.metersPerPixel
      area.style.width = area.style.height = `${Math.round(diameter)}px`
      quietMarker(new this.engine.Marker({ element: area, anchor: "center" }).setLngLat([ this.lngValue, this.latValue ]).addTo(this.map))
    } else {
      // Brand-orange pin at the deal location (decoration: the whole card is the link).
      const marker = quietMarker(new this.engine.Marker({ color: "#FF8A1A" }).setLngLat([ this.lngValue, this.latValue ]).addTo(this.map))
      // Just published: the pin drops onto the map once and a ring spreads from it (CSS, static-map-marker--arrival).
      if (this.arrivalValue) this.markArrival(marker.getElement())
    }

    // If the container had no height at init (e.g. inside a not-yet-laid-out section), resize once
    // after layout so the thumbnail never renders blank.
    this.map.on("load", () => requestAnimationFrame(() => this.map?.resize()))

    // Re-apply pixelRatio on DPR change so the never-moving thumbnail's canvas backing store can't go stale
    // (page-zoom / display move / devtools device-mode) — region_map already did this; static_map did not.
    this._teardownDpr = setupDprResize(this.map)
  }

  get zoom() {
    return this.hasApproximateMValue ? Math.min(this.zoomValue, this.constructor.APPROXIMATE_MAX_ZOOM) : this.zoomValue
  }

  // Web Mercator ground resolution for MapLibre's 512px tiles at this latitude and zoom
  get metersPerPixel() {
    return 78271.517 * Math.cos(this.latValue * Math.PI / 180) / 2 ** this.zoom
  }

  disconnect() {
    this.observer?.disconnect()
    this.arrivalObserver?.disconnect()
    this._teardownDpr?.()
    this.map?.remove() // free the WebGL context
  }

  // Plays once the tiles are in and the card is at least half on screen (it usually sits below the fold under the
  // publish cue), then drops ?from=wizard so a reload doesn't replay it.
  markArrival(element) {
    this.map.once("load", () => {
      this.arrivalObserver = new IntersectionObserver(([ entry ]) => {
        if (!entry.isIntersecting) return
        this.arrivalObserver.disconnect()
        element.classList.add("static-map-marker--arrival")
        const ring = document.createElement("span")
        ring.className = "static-map-arrival-ring"
        ring.setAttribute("aria-hidden", "true")
        element.appendChild(ring)
        const url = new URL(window.location.href)
        if (url.searchParams.get("from") === "wizard") {
          url.searchParams.delete("from")
          window.history.replaceState(window.history.state, "", url)
        }
      }, { threshold: 0.5 })
      this.arrivalObserver.observe(this.element)
    })
  }
}
